import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import {
  EReduxState,
  getBooleanStates,
  TReduxBooleanState
} from 'redux/utils/State'

import { useAnalysisActions } from 'redux/actions/Analyses'
import { useTaskActions } from 'redux/actions/Tasks'

import { useTranslation } from 'language/translator'

import { ApplicationState } from 'redux/Stores/types'

import { TTaskResponse } from 'redux/api/Tasks'
import {
  selectAnalyses,
  TAnalysisSelectorResponse
} from 'redux/selectors/Analyses'
import {
  selectTask,
  selectTasks,
  selectTasksFetchStatus
} from 'redux/selectors/Tasks'

/** A task with the analysis it was created from, one step deep. */
export type TTask = TTaskResponse & {
  /** Null unless created from an analysis, or until the analyses load. */
  analysis: TAnalysisSelectorResponse | null
}

type TTasksState = { tasks: TTask[] } & TReduxBooleanState

const toTask = (
  task: TTaskResponse,
  analyses: TAnalysisSelectorResponse[]
): TTask => ({
  ...task,
  analysis:
    task.source_type === 'ANALYSIS' && task.source_id
      ? analyses.find((item) => item.id === task.source_id) ?? null
      : null
})

/**
 * Fetches the tasks and the analyses they are joined with. Only the tasks
 * count towards loading: `analysis` is nullable, so a list can render before
 * it lands.
 */
const useTasksFetchState = (): TReduxBooleanState & {
  fetchStatus: EReduxState
} => {
  const { tryGetAllAnalyses } = useAnalysisActions()
  const { tryGetAllTasks } = useTaskActions()

  const fetchStatus = useSelector(selectTasksFetchStatus)

  useEffect(() => {
    tryGetAllTasks()
    tryGetAllAnalyses()
  }, [tryGetAllAnalyses, tryGetAllTasks])

  return { fetchStatus, ...getBooleanStates(fetchStatus) }
}

/** The tasks, empty until they have loaded. */
export const useTasks = (): TTasksState => {
  const tasks = useSelector(selectTasks)
  const { lang } = useTranslation()
  const analyses = useSelector((state: ApplicationState) =>
    selectAnalyses(state, lang)
  )
  const { fetchStatus, ...state } = useTasksFetchState()

  return {
    tasks:
      fetchStatus === EReduxState.SUCCESS
        ? tasks.map((task) => toTask(task, analyses))
        : [],
    ...state
  }
}

export const useTasksFromKeyMetric = (
  keyMetricId?: string | null
): TTasksState => {
  const { tasks, ...state } = useTasks()

  return {
    ...state,
    tasks: tasks.filter((task) => task.key_metric_id === keyMetricId)
  }
}

export const useTasksFromReportEdition = (
  reportEditionId?: string | null
): TTasksState => {
  const { tasks, ...state } = useTasks()

  return {
    ...state,
    tasks: tasks.filter((task) => task.source_id === reportEditionId)
  }
}

export const useTask = (id: string | null): TTask | null => {
  const task = useSelector((state: ApplicationState) => selectTask(state, id))
  const { lang } = useTranslation()
  const analyses = useSelector((state: ApplicationState) =>
    selectAnalyses(state, lang)
  )

  useTasksFetchState()

  return task ? toTask(task, analyses) : null
}
